import 'reflect-metadata';

import {
  IPasswordUpdateRequest,
  IUserDetailsUpdate,
} from 'src/interfaces/user';

import {
  IAccessToken,
  ICreateUserForExternalRequest,
} from 'src/interfaces/authentication';
import { IInvitedUser } from 'src/interfaces/invited-user';
import { IMarketingOptInRequest } from 'src/interfaces/user/user.interface';
import { injectable } from 'inversify';
import { IIdentityService } from './interfaces';
import ApiClient from './api-client';
import {
  IApiResponse,
  ILoginResponse,
  IRefreshTokenResponse,
} from './interfaces/identity-service.interface';

@injectable()
class IdentityService implements IIdentityService {
  private apiClient: ApiClient;

  public constructor() {
    this.apiClient = new ApiClient(process.env.IDENTITY_API_BASE_URL ?? '');
  }

  public async registerExternalUser(
    request: ICreateUserForExternalRequest
  ): Promise<ILoginResponse> {
    const authResponse =
      await this.apiClient.postWithResponse<IIdentityTokenDataWrapper>(
        'user/getorcreateuserforexternal',
        request
      );

    if (
      authResponse.data &&
      authResponse.status.toLowerCase() === 'success' &&
      typeof authResponse.data !== 'string'
    ) {
      const accessTokenResponse =
        await this.apiClient.postWithResponse<IAccessTokenDataWrapper>(
          'user/accesstoken',
          {
            tenantId: request.tenantId,
            packageId: request.packageId,
            idToken: authResponse.data.token,
            audience: 'string',
          }
        );
      const { accessToken, refreshToken, expiresIn } =
        accessTokenResponse.data ?? {};
      return {
        accessToken,
        accessTokenExpiresIn: expiresIn,
        refreshToken,
        success: !!accessToken,
        message: accessTokenResponse.status,
      };
    }
    return { success: false };
  }

  /**
   * OTT-1985 — three defects fixed here.
   *
   * Path: `IDENTITY_API_BASE_URL` already ends in `/api/`, so the correct value
   * is 'token/refresh' (the backend route is `api/token/refresh`). The previous
   * 'refresh' was missing the `token/` segment — not, as the old comment
   * claimed, an `/api` prefix.
   *
   * Body: the backend's request validator requires `tenantId` (non-empty) and
   * `refreshToken` (non-null), and `accessToken` must be the REAL (expired)
   * token string because `RefreshAsync` decodes it to obtain the user ID.
   *
   * Status: lowercased to match the sibling calls to this same backend.
   *
   * The response's `expiresIn` is deliberately NOT read: the backend never sets
   * it on this endpoint, so it always serialises as 0. Callers derive expiry
   * from the returned JWT instead.
   */
  public async refreshToken(
    accessToken: string,
    refreshToken: string,
    tenantId: string,
    packageId: string
  ): Promise<IRefreshTokenResponse> {
    const response =
      await this.apiClient.postWithResponse<IAccessTokenDataWrapper>(
        'token/refresh',
        { accessToken, refreshToken, tenantId, packageId }
      );

    if (response.status?.toLowerCase() === 'success') {
      const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
        response.data ?? {};

      return {
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
        success: true,
      };
    }

    return { success: false };
  }

  public async login(
    tenantId: string,
    packageId: string,
    email: string,
    password: string
  ): Promise<ILoginResponse> {
    const authResponse =
      await this.apiClient.postWithResponse<IIdentityTokenDataWrapper>(
        'user/authenticate',
        { emailAddress: email, password }
      );

    if (
      authResponse.data &&
      authResponse.status.toLowerCase() === 'success' &&
      typeof authResponse.data !== 'string'
    ) {
      const accessTokenResponse =
        await this.apiClient.postWithResponse<IAccessTokenDataWrapper>(
          'user/accesstoken',
          {
            tenantId,
            packageId,
            idToken: authResponse.data.token,
            audience: 'string',
          }
        );

      const { accessToken, refreshToken, expiresIn } =
        accessTokenResponse.data ?? {};

      return {
        accessToken,
        accessTokenExpiresIn: expiresIn,
        refreshToken,
        success: !!accessToken,
        message: accessTokenResponse.status,
      };
    }

    return {
      success: false,
      message: authResponse.status,
    };
  }

  public async forgotPassword(
    emailAddress: string,
    passwordResetUrl: string,
    tenantId?: string,
    packageId?: string,
    country?: string,
    language?: string
  ): Promise<IApiResponse> {
    const response = await this.apiClient.post('user/requestpasswordreset', {
      tenantId,
      packageId,
      emailAddress,
      passwordResetUrl,
      country,
      language,
      product: 'ProAccount',
    });

    return response;
  }

  public async resetPassword(
    resetToken: string,
    newPassword: string,
    emailAddress: string
  ): Promise<IApiResponse> {
    const response = await this.apiClient.patch('user/resetpassword', {
      resetToken,
      newPassword,
      emailAddress,
    });

    return response;
  }

  public async updateUserDetails(
    updatedUserDetails: IUserDetailsUpdate
  ): Promise<IApiResponse> {
    const response = await this.apiClient.patch('user/updatedetails', {
      ...updatedUserDetails,
    });

    return response;
  }

  public async updatePassword(
    updatedPassword: IPasswordUpdateRequest
  ): Promise<IApiResponse> {
    const response = await this.apiClient.patch('user/updatepassword', {
      ...updatedPassword,
    });

    return response;
  }

  public async updateMarketingOptIn(
    marketingOptIn: IMarketingOptInRequest
  ): Promise<IApiResponse> {
    const response = await this.apiClient.patch('user/updatedetails', {
      ...marketingOptIn,
    });

    return response;
  }

  public async getInvitedUser(id: string): Promise<IInvitedUser | undefined> {
    const response = await this.apiClient.get<IInvitedUserDataWrapper>(
      `invited-user/${id}`
    );

    return response?.data?.invitedUser;
  }

  public async registerInvitedUser(
    id: string,
    password: string,
    marketingOptIn: boolean
  ): Promise<IApiResponse> {
    const response =
      await this.apiClient.postWithResponse<IRegisterInvitedUserDataWrapper>(
        'invited-user/register',
        { id, password, marketingOptIn }
      );

    return { success: !!response.data?.id };
  }

  public async linkInvitedUserToExistingUser(
    invitedUserId: string
  ): Promise<IApiResponse> {
    const response =
      await this.apiClient.postWithResponse<ILinkInvitedUserDataWrapper>(
        'invited-user/link-to-existing-user',
        { invitedUserId }
      );

    return { success: !!response.data?.isOrchaUserLinked };
  }
}

interface IIdentityTokenDataWrapper {
  data?: { token: string; expiresIn: number } | string;
  status: string;
}

interface IAccessTokenDataWrapper {
  data?: IAccessToken;
  status: string;
}

export interface IInvitedUserDataWrapper {
  data?: { invitedUser?: IInvitedUser };
}

export interface IRegisterInvitedUserDataWrapper {
  data?: { id?: string };
}

export interface ILinkInvitedUserDataWrapper {
  data?: { isOrchaUserLinked: boolean };
}

export default IdentityService;
